import * as moment from "moment";
import * as React from "react";
import * as Modal from "react-modal";
import * as Constants from "../../core/constants";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IRecipient from "../../types/IRecipient";

interface IRecipientDetailColumn {
    key: string;
    value: string | number | React.ReactNode;
}

interface IRecipientDetailModalProps extends ITranslationProps {
    recipient: IRecipient | null;
    messageType: string;
    isOpen: boolean;
    onRequestClose: () => void;
}

function RecipientDetailModal(p: IRecipientDetailModalProps) {
    return (
        <Modal isOpen={p.isOpen}
            onRequestClose={p.onRequestClose}
            style={{
                content: {
                    position: "relative",
                    top: "auto",
                    bottom: "auto",
                    left: "auto",
                    right: "auto",
                    maxWidth: "1200px",
                    margin: "1.75rem auto",
                    padding: "10px"
                },
                overlay: {
                    zIndex: 10
                }
            }}>
            <div className="modal-header">
                <h5 className="modal-title"> {p.t("RecipientDetail.RecipientDetails")}</h5>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={p.onRequestClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div className="modal-body">
                {p.recipient && getDetails()}
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={p.onRequestClose}>{p.t("Global.Close")}</button>
            </div>
        </Modal>
    );


    function getDetails(): React.ReactNode {
        if (!p.recipient) { return ""; }
        const cols: IRecipientDetailColumn[] = [];

        if (p.messageType === Constants.MESSAGE_TYPE_VOICE) {
            cols.push({ key: "RecipientDetail.StartTime", value: formatDate(p.recipient.startTime) });
            cols.push({ key: "RecipientDetail.EndTime", value: formatDate(p.recipient.endTime) });
            cols.push({ key: "RecipientDetail.Duration", value: p.recipient.duration + " " + p.t("Global.Seconds") });
            cols.push({ key: "RecipientDetail.PhoneNumber", value: p.recipient.phoneNumber });
            cols.push({ key: "RecipientDetail.Queued", value: formatBoolean(p.recipient.queued) });
            cols.push({ key: "RecipientDetail.ErrorMessage", value: p.recipient.errorMessage });
            cols.push({ key: "RecipientDetail.NoResponseNotifyDate", value: formatDate(p.recipient.noResponseNotifySentDateTime) });
            cols.push({ key: "RecipientDetail.SentToProvider", value: formatBoolean(p.recipient.sentToProvider) });
            cols.push({ key: "RecipientDetail.DigitsDialed", value: p.recipient.digitsDialed ? p.recipient.digitsDialed.split("|").join(", ") : "" });
            cols.push({ key: "MessageDetail.MachineDetection", value: p.recipient.machineDetection });
            cols.push({ key: "RecipientDetail.ResponseCode", value: p.recipient.responseCode });
        }

        if (p.messageType === Constants.MESSAGE_TYPE_EMAIL) {
            cols.push({ key: "RecipientDetail.SentDate", value: formatDate(p.recipient.sentDateTime) });
            cols.push({ key: "RecipientDetail.To", value: p.recipient.to });
            cols.push({ key: "RecipientDetail.RecipientType", value: p.recipient.recipientType });
            cols.push({ key: "RecipientDetail.EmailSent", value: formatBoolean(p.recipient.emailSent) });
            cols.push({ key: "RecipientDetail.Queued", value: formatBoolean(p.recipient.queued) });
            cols.push({ key: "RecipientDetail.ErrorMessage", value: p.recipient.errorMessage });
            cols.push({ key: "RecipientDetail.NoResponseNotifyDate", value: formatDate(p.recipient.noResponseNotifySentDateTime) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptReceived", value: formatBoolean(p.recipient.deliveryReceiptReceived) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptDate", value: formatDate(p.recipient.deliveryReceiptDateTime) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptMessage", value: p.recipient.deliveryReceiptMessage });
            cols.push({ key: "MessageDetail.Bounced", value: formatBoolean(p.recipient.bounced) });
            cols.push({ key: "RecipientDetail.BouncedDate", value: formatDate(p.recipient.bouncedDateTime) });
            cols.push({ key: "RecipientDetail.NumberOfAttempts", value: p.recipient.numberOfAttemptsTried ? p.recipient.numberOfAttemptsTried : "" });
            cols.push({ key: "RecipientDetail.ResponseReceived", value: formatBoolean(p.recipient.responseReceived) });
            cols.push({ key: "RecipientDetail.ResponseDate", value: formatDate(p.recipient.responseDateTime) });
            cols.push({ key: "RecipientDetail.ResponseMessage", value: p.recipient.responseMessage });
        }

        if (p.messageType === Constants.MESSAGE_TYPE_SMS) {
            cols.push({ key: "RecipientDetail.SentDate", value: formatDate(p.recipient.sentDateTime) });
            cols.push({ key: "RecipientDetail.PhoneNumber", value: p.recipient.phoneNumber });
            cols.push({ key: "RecipientDetail.MessageSent", value: formatBoolean(p.recipient.messageSent) });
            cols.push({ key: "RecipientDetail.SentToProvider", value: formatBoolean(p.recipient.sentToProvider) });
            cols.push({ key: "RecipientDetail.Queued", value: formatBoolean(p.recipient.queued) });
            cols.push({ key: "RecipientDetail.ErrorMessage", value: p.recipient.errorMessage });
            cols.push({ key: "RecipientDetail.NoResponseNotifyDate", value: formatDate(p.recipient.noResponseNotifySentDateTime) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptReceived", value: formatBoolean(p.recipient.deliveryReceiptReceived) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptDate", value: formatDate(p.recipient.deliveryReceiptDateTime) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptMessage", value: p.recipient.deliveryReceiptMessage });
            cols.push({ key: "RecipientDetail.DeliveryReceiptCode", value: p.recipient.deliveryReceiptCode });
            cols.push({ key: "RecipientDetail.DeliveryReceiptRaw", value: p.recipient.deliveryReceiptRawPostBody });
            cols.push({ key: "RecipientDetail.ResponseReceived", value: formatBoolean(p.recipient.responseReceived) });
            cols.push({ key: "RecipientDetail.ResponseDate", value: formatDate(p.recipient.responseDateTime) });
            cols.push({ key: "RecipientDetail.ResponseMessage", value: p.recipient.responseMessage });
        }

        if (p.messageType === Constants.MESSAGE_TYPE_PUSH) {
            cols.push({ key: "RecipientDetail.SentDate", value: formatDate(p.recipient.sentDateTime) });
            cols.push({ key: "RecipientDetail.Queued", value: formatBoolean(p.recipient.queued) });
            cols.push({ key: "RecipientDetail.ErrorMessage", value: p.recipient.errorMessage });
            cols.push({ key: "RecipientDetail.NoResponseNotifyDate", value: formatDate(p.recipient.noResponseNotifySentDateTime) });
            cols.push({ key: "RecipientDetail.Platform", value: p.recipient.platform });
            cols.push({ key: "RecipientDetail.DeliveryReceiptReceived", value: formatBoolean(p.recipient.deliveryReceiptReceived) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptDate", value: formatDate(p.recipient.deliveryReceiptDateTime) });
            cols.push({ key: "RecipientDetail.DeliveryReceiptMessage", value: p.recipient.deliveryReceiptMessage });
            cols.push({ key: "RecipientDetail.ResponseReceived", value: formatBoolean(p.recipient.responseReceived) });
            cols.push({ key: "RecipientDetail.ResponseDate", value: formatDate(p.recipient.responseDateTime) });
            cols.push({ key: "RecipientDetail.ResponseMessage", value: p.recipient.responseMessage });
        }

        const colElements: React.ReactNode[] = [];
        cols.forEach((col) => {
            colElements.push(
                <div key={`${col.key}1`} className="col-md-3"><strong>{p.t(col.key)}</strong></div>,
                <div key={`${col.key}2`} className="col-md-9">{col.value}</div>);
        });

        return <div className="row">{colElements}</div>;
    }

    function formatDate(value: string): string {
        if (value) {
            return moment(value).format(Constants.MOMENT_FORMAT_DATETIME);
        }
        return value;
    }

    function formatBoolean(value: boolean) {
        return value ? p.t("Global.Yes") : p.t("Global.No");
    }




}


export default translateComponent(RecipientDetailModal);



// WEBPACK FOOTER //
// ./src/components/MessageDetail/RecipientDetailModal.tsx